import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Button } from 'react-aria-components';
import { cx } from './cx';
import { Icon, type IconName } from './Icon';
import s from './Toast.module.css';

// React Aria's Toast is still UNSTABLE_ in 1.22, so toasts use a plain polite live region.

export type ToastTone = 'done' | 'info' | 'problem';

export interface ToastMessage {
  tone: ToastTone;
  /** Short and plain: "Saved", "Budget updated". */
  title: string;
  body?: string;
  /** Stays until dismissed (by the person or by `dismiss`), e.g. while work is still going. */
  sticky?: boolean;
}

interface QueuedToast extends ToastMessage {
  id: number;
}

export interface ToastApi {
  /** Shows a toast; it hides itself after a few seconds unless hovered or focused. */
  show: (message: ToastMessage) => number;
  /** Takes a toast away (a no-op once it has gone). */
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi>({ show: () => 0, dismiss: () => undefined });

const TONE_ICON: Record<ToastTone, IconName> = { done: 'check', info: 'help', problem: 'close' };
const TONE_CLASS: Record<ToastTone, string | undefined> = {
  done: s.done,
  info: s.info,
  problem: s.problem,
};
const VISIBLE_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toasts, setToasts] = useState<readonly QueuedToast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((message: ToastMessage) => {
    const id = nextId.current++;
    // Only ordinary toasts count against the three slots; one that is waiting on work stays.
    setToasts((current) => {
      const sticky = current.filter((t) => t.sticky === true);
      const ordinary = current.filter((t) => t.sticky !== true);
      const kept = message.sticky === true ? ordinary.slice(-3) : ordinary.slice(-2);
      return [...sticky, ...kept, { ...message, id }].sort((a, b) => a.id - b.id);
    });
    return id;
  }, []);

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <section
        className={s.region}
        aria-label="Messages"
        aria-live="polite"
        role="status"
        data-feedback-ui
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </section>
    </ToastContext.Provider>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: QueuedToast;
  onDismiss: (id: number) => void;
}): ReactElement {
  const [paused, setPaused] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Hovering or focusing a toast keeps it on screen (WCAG 2.2.1: enough time to read it).
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const pause = (): void => {
      setPaused(true);
    };
    const resume = (): void => {
      setPaused(false);
    };
    element.addEventListener('mouseenter', pause);
    element.addEventListener('mouseleave', resume);
    element.addEventListener('focusin', pause);
    element.addEventListener('focusout', resume);
    return () => {
      element.removeEventListener('mouseenter', pause);
      element.removeEventListener('mouseleave', resume);
      element.removeEventListener('focusin', pause);
      element.removeEventListener('focusout', resume);
    };
  }, []);

  useEffect(() => {
    if (paused || toast.sticky === true) return;
    const timer = window.setTimeout(() => {
      onDismiss(toast.id);
    }, VISIBLE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [paused, toast.id, toast.sticky, onDismiss]);

  return (
    <div ref={ref} className={cx(s.toast, TONE_CLASS[toast.tone])}>
      <span className={s.icon} aria-hidden="true">
        <Icon name={TONE_ICON[toast.tone]} size={16} strokeWidth={2.4} />
      </span>
      <div className={s.text}>
        <span className={s.title}>{toast.title}</span>
        {toast.body !== undefined && <span className={s.body}>{toast.body}</span>}
      </div>
      <Button
        className={s.close}
        aria-label={`Dismiss: ${toast.title}`}
        onPress={() => {
          onDismiss(toast.id);
        }}
      >
        <Icon name="close" size={16} />
      </Button>
    </div>
  );
}

/** Shows short confirmations ("Saved") in the corner. */
export function useToast(): ToastApi {
  return useContext(ToastContext);
}
